import {
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import {
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import {
  getIsCustomRequirement,
  getIsGeneralRequirement,
  getRequirementListKey
} from "../utils";
import { RequirementsEmptyState } from "./RequirementsEmptyState";

export type RequirementsTableProps = {
  requirements: Array<ExtendedRequirementTableItemData>;
  contextType: RequirementContextType;
  hideActions?: boolean;
  compact?: boolean;
  hideTaxVerificationConsent?: boolean;
  requiredAction?: string;
  showEngagementsView?: boolean;
  getOnViewDetails: (
    item: IRequirementResponse,
    additionalData?: RequirementAdditionalData
  ) => ((item: IRequirementResponse) => void) | undefined;
};

export const ExtendedRequirementsTable: React.FC<RequirementsTableProps> = ({
  requirements,
  contextType,
  hideActions,
  compact,
  hideTaxVerificationConsent,
  getOnViewDetails,
  showEngagementsView,
  requiredAction
}) => {
  const requirementsConfig = useRequirementsConfig({
    requirements: requirements.filter(
      requirement => "requirementId" in requirement
    ) as RequirementTableItemData[],
    contextType,
    context: {
      hideTaxVerificationConsent
    }
  });

  const tableData: WSTableItem<ExtendedRequirementTableItemData>[] =
    requirements.map((requirement, index) => {
      return {
        id: getRequirementListKey(requirement, index),
        data: requirement
      };
    });

  const columns: Array<WSTableColumn<ExtendedRequirementTableItemData>> = [
    {
      config: {
        header: {
          text: "Requirement"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.nameCell} />;
        }

        return (
          <WSTableCell {...requirementsConfig.getNameCellProps(rowItem.data)} />
        );
      }
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: compact ? ["XS", "S", "M", "L", "XL"] : ["XS"],
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.typeCell} />;
        }

        return (
          <WSTableCell {...requirementsConfig.getTypeCellProps(rowItem.data)} />
        );
      }
    },
    {
      config: {
        hideOnScreens:
          showEngagementsView || contextType === RequirementContextType.Payee
            ? ["XS", "S", "M", "L", "XL"]
            : [],
        header: {
          text: "Clients"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.clientsCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getClientsCellProps(rowItem.data)}
          />
        );
      }
    },
    {
      config: {
        hideOnScreens:
          !showEngagementsView && contextType === RequirementContextType.Payer
            ? ["XS", "S", "M", "L", "XL"]
            : [],
        header: {
          text: "Engagements"
        },
        gridTemplateSizeMax: "0.4fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.engagementsCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getEngagementsCellProps(rowItem.data)}
          />
        );
      }
    },
    {
      config: {
        header: {
          text: "Status"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => {
        if (getIsCustomRequirement(rowItem.data)) {
          return <WSTableCell {...rowItem.data.statusCell} />;
        }

        return (
          <WSTableCell
            {...requirementsConfig.getStatusCellProps(rowItem.data)}
          />
        );
      }
    }
  ];

  if (requirements.length === 0) {
    return <RequirementsEmptyState />;
  }

  return (
    <WSTable<ExtendedRequirementTableItemData>
      loading={requirementsConfig.isLoading}
      key={requirementsConfig.dataUpdatedAt}
      getOnRowClick={({ data }) => {
        const requirement = data;

        const hasViewDetails =
          !getIsCustomRequirement(requirement) &&
          !getIsGeneralRequirement(requirement);

        if (!hasViewDetails) {
          return undefined;
        }

        const additionalData =
          requirementsConfig.getAdditionalData(requirement);
        const callback = getOnViewDetails(requirement, additionalData);

        return callback
          ? ({ data }) => callback(data as IRequirementResponse)
          : undefined;
      }}
      rowActions={({ data }) => {
        if (getIsCustomRequirement(data)) {
          return [];
        }

        if (hideActions || !requiredAction) {
          return [];
        }

        const isActionRequired = requirementsConfig.isActionRequired(data);

        if (isActionRequired) {
          return [
            {
              text: requiredAction,
              onClick: () => {
                const additionalData =
                  requirementsConfig.getAdditionalData(data);
                const callback = getOnViewDetails(
                  data as IRequirementResponse,
                  additionalData
                );

                return callback && callback(data as IRequirementResponse);
              }
            }
          ];
        }

        return [];
      }}
      rowMenuActions={({ data }) => {
        if (getIsCustomRequirement(data)) {
          return data.menuActions || [];
        }
        if (hideActions) {
          return [];
        }

        const veiwDetails: WSTableRowMenuAction<RequirementTableItemData> = {
          label: "View requirement",
          icon: "view",
          onClick: () => {
            const additionalData = requirementsConfig.getAdditionalData(data);

            const callback = getOnViewDetails(
              data as IRequirementResponse,
              additionalData
            );

            return callback && callback(data as IRequirementResponse);
          }
        };

        const requirement = data as IRequirementResponse;

        const hasViewDetails =
          !getIsCustomRequirement(data) && !!getOnViewDetails(data);

        if (hasViewDetails && !getIsGeneralRequirement(requirement)) {
          return [
            veiwDetails,
            ...requirementsConfig.renderRowMenuActions(data)
          ];
        }

        return requirementsConfig.renderRowMenuActions(data);
      }}
      columns={columns}
      tableData={tableData}
      data-testid="requirementsTable"
    />
  );
};
